/* Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { TickFormatter } from "@visx/axis";
import { NumberValue } from "d3-scale";
import { Box, Col, FlexContainer, Row, Text, theme } from "@nordcloud/gnui";
import {
  ClusterConsumedResource,
  StorageReadWriteResource,
  useQueryClusterResourceConsumptionQuery,
} from "~/generated/graphql";
import { LoaderWrap, Legend } from "~/components";
import { isNotEmpty } from "~/tools";
import {
  CHART_COLORS,
  ConsumptionChart,
  ConsumptionChartProps,
} from "~/views/estate/EstateDetailsPage/components/ConsumptionChart";
import { byteSize } from "~/views/estate/EstateDetailsPage/utils";

const chartLegends = {
  cpuMemory: [
    {
      color: CHART_COLORS.used,
      opacity: 0.6,
      label: "Avg. Used",
    },
  ],
  storage: [
    {
      color: CHART_COLORS.used,
      opacity: 0.6,
      label: "Read",
    },
    {
      color: CHART_COLORS.requested,
      opacity: 0.6,
      label: "Write",
    },
  ],
};

const cpuMemoryLegend = () => <Legend inline data={chartLegends.cpuMemory} />;

const storageLegend = () => <Legend inline data={chartLegends.storage} />;

type ChartProps = {
  caption: string;
  data: ClusterConsumedResource[] | StorageReadWriteResource[];
  legend?: () => JSX.Element;
  axisFormatter?: TickFormatter<NumberValue>;
  isNode?: boolean;
};

function Chart(props: ChartProps) {
  const { caption, data, legend, axisFormatter } = props;
  const chartProps: ConsumptionChartProps = {
    width: 400,
    height: 120,
    data,
    ...(axisFormatter && { axisFormatter }),
  };
  return (
    <>
      <FlexContainer justifyContent="space-between">
        <Text size="sm" tag="div" weight="medium">
          {caption}
        </Text>
      </FlexContainer>
      <ConsumptionChart {...chartProps} />
      {isNotEmpty(data) && legend && legend()}
    </>
  );
}

type UtilizationProps = {
  nordcloudId: string;
};

export function ClusterUtilization({ nordcloudId }: UtilizationProps) {
  const { data, loading } = useQueryClusterResourceConsumptionQuery({
    variables: {
      id: nordcloudId,
    },
  });

  return (
    <Row>
      <Col>
        <Box spacing="spacing04" css={{ overflowX: "scroll" }}>
          <LoaderWrap inContent loading={loading}>
            <Box
              boxStyle="grey"
              px={theme.spacing.spacing03}
              py={theme.spacing.spacing02}
              spacing="spacing04"
            >
              <FlexContainer justifyContent="space-between">
                <Text size="sm" tag="div" weight="medium">
                  Utilization Information
                </Text>
              </FlexContainer>
            </Box>
            <Row>
              <Col>
                <Chart
                  isNode
                  caption="CPU"
                  data={data?.clusterResourceConsumption?.cpu || []}
                  legend={cpuMemoryLegend}
                  axisFormatter={(n) => `${n}m`}
                />
              </Col>
              <Col>
                <Chart
                  caption="Storage"
                  data={data?.clusterResourceConsumption?.storageRw || []}
                  legend={storageLegend}
                  axisFormatter={(n) => byteSize(n.valueOf())}
                />
              </Col>
              <Col>
                <Chart
                  isNode
                  caption="Memory"
                  data={data?.clusterResourceConsumption?.memory ?? []}
                  legend={cpuMemoryLegend}
                  axisFormatter={(n) => byteSize(n.valueOf())}
                />
              </Col>
            </Row>
          </LoaderWrap>
        </Box>
      </Col>
    </Row>
  );
}
